import { useLayoutEffect, useState } from "react";

import {
	LISTING_PAGE_SIZE,
	LISTING_STEP_BELOW_LG
} from "@/domain/product-listing/listing-depth";

const ALL = Number.POSITIVE_INFINITY;

/** A first page starts at one step; a deeper window shows everything it rendered. */
export function initialShownBelowLg(firstPage: number, cards: number): number {
	return firstPage === 1 && cards <= LISTING_PAGE_SIZE
		? LISTING_STEP_BELOW_LG
		: ALL;
}

function readStored(key: string): number | undefined {
	try {
		const stored = window.sessionStorage.getItem(key);
		return stored === null ? undefined : Number(stored);
	} catch {
		return undefined;
	}
}

function writeStored(key: string, shown: number): void {
	try {
		window.sessionStorage.setItem(key, String(shown));
	} catch {
		// A full or blocked storage only costs restoring the place on Back.
	}
}

/**
 * How many cards a shopper below lg has revealed for one listing and filter
 * state. Kept for the tab so Back from a product finds the same cards showing.
 */
export function useShownBelowLg(
	key: string,
	initial: number
): [number, (shown: number) => void] {
	const [entry, setEntry] = useState({ key, shown: initial });

	useLayoutEffect(() => {
		const stored = readStored(key);
		setEntry((current) =>
			current.key === key && stored === undefined
				? current
				: { key, shown: stored ?? LISTING_STEP_BELOW_LG }
		);
	}, [key]);

	function setShown(shown: number): void {
		setEntry({ key, shown });
		writeStored(key, shown);
	}

	return [entry.key === key ? entry.shown : LISTING_STEP_BELOW_LG, setShown];
}
